// src/lib/lead/actions/updateLeadField.ts
//
// Central funnel for every UI write to `currentLead.extracted`. Phase 1 of
// transcript merge v2 (docs/planning/transcript_merge_v2.md) adds two
// responsibilities to this function:
//
//  1. Write `user_overrides[field]` with explicit user intent (`manual` when
//     the new value is meaningful, `cleared` when blank/null). The legacy
//     `lockedFields` map is still written so untouched call sites keep
//     working — the merge function reads `user_overrides` preferentially
//     and falls back to `lockedFields` for old leads.
//  2. Optionally emit a diagnostic record (Phase 0) so the dev ring buffer
//     can capture what "cleared" actually looks like (null vs "") in real
//     sessions.

import type {
  ExtractedLeadV2,
  Lead,
  LeadUserOverride,
  LeadUserOverrides,
  LockedFields,
} from "@/types/lead";

export type IsMeaningful = (v: unknown) => boolean;

export type UpdateLeadFieldDeps = {
  /** Normalizes phone strings when managers type them. */
  formatPhoneStrict: (raw: string | null | undefined) => string | null;
  isMeaningful: IsMeaningful;
  /** Merge-suggestion re-eval gate (e.g. only for fields that affect duplicate detection). */
  shouldTriggerMergeSuggestion: (field: keyof ExtractedLeadV2) => boolean;
  /**
   * Auth user id used to stamp the override. Optional — call sites that
   * can't resolve the id (rare) pass `null` and the override still
   * records the status + timestamp.
   */
  getActorUserId?: () => string | null;
  /**
   * Phase 0 diagnostic recorder. Off by default in production. Call sites
   * pass the ring-buffer recorder behind a dev-mode gate.
   */
  recordFieldEdit?: (entry: { key: string; before: unknown; after: unknown }) => void;
};

export type UpdateLeadFieldResult = {
  updatedLead: Lead;
  nextLocks: LockedFields;
  nextName: string;
  shouldRecomputeMergeSuggestion: boolean;
};

export function updateLeadField(
  currentLead: Lead,
  field: keyof ExtractedLeadV2,
  value: unknown,
  deps: UpdateLeadFieldDeps,
): UpdateLeadFieldResult {
  // Treat a missing extracted as an empty object so manual edits on a freshly
  // created "New Customer" lead (where createNewLead() sets extracted=null)
  // build up extraction incrementally instead of being silently dropped.
  const baseExtracted: ExtractedLeadV2 =
    currentLead.extracted ?? ({} as ExtractedLeadV2);

  let nextValue: unknown = value;
  if (field === "phone") {
    nextValue = deps.formatPhoneStrict(nextValue as string | null | undefined);
  }

  const prevLocks = (currentLead.lockedFields || {}) as LockedFields;
  const nextLocks: LockedFields = { ...prevLocks };
  if (deps.isMeaningful(nextValue)) {
    nextLocks[field] = true;
  } else {
    delete nextLocks[field];
  }

  // Phase 1: maintain `user_overrides` alongside `lockedFields`. Meaningful
  // writes record `manual`; blank/null writes record `cleared` so the
  // transcript merge can refill on next re-extract.
  const prevOverrides: LeadUserOverrides = currentLead.user_overrides ?? {};
  const nextOverrides: LeadUserOverrides = { ...prevOverrides };
  const now = new Date().toISOString();
  const actorId = deps.getActorUserId?.() ?? null;
  const isMeaningfulNext = deps.isMeaningful(nextValue);
  const override: LeadUserOverride = isMeaningfulNext
    ? {
        status: "manual",
        value: normalizeOverrideValue(nextValue),
        updated_at: now,
        updated_by_user_id: actorId,
      }
    : {
        status: "cleared",
        updated_at: now,
        updated_by_user_id: actorId,
      };
  nextOverrides[field] = override;

  const updatedExtracted: ExtractedLeadV2 = {
    ...baseExtracted,
    [field]: nextValue as never,
  };

  // Sync lead.name when the manager edits first/last name.
  let nextName = currentLead.name;
  if (field === "first_name" || field === "last_name") {
    const fullName = [updatedExtracted.first_name, updatedExtracted.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (fullName) nextName = fullName;
  }

  if (deps.recordFieldEdit) {
    deps.recordFieldEdit({
      key: field as string,
      before: baseExtracted[field],
      after: nextValue,
    });
  }

  const shouldRecomputeMergeSuggestion = deps.shouldTriggerMergeSuggestion(field);

  const updatedLead: Lead = {
    ...currentLead,
    extracted: updatedExtracted,
    name: nextName,
    lastUpdated: Date.now(),
    lockedFields: nextLocks,
    user_overrides: nextOverrides,
  };

  return { updatedLead, nextLocks, nextName, shouldRecomputeMergeSuggestion };
}

/**
 * Coerce the override's stored `value` into the union the type allows.
 * Non-string, non-array values are stringified so the override stays
 * comparable on re-extract.
 */
function normalizeOverrideValue(v: unknown): string | string[] | null {
  if (v == null) return null;
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map((it) => String(it));
  return String(v);
}
